// Layer 4 (UI) — the AI update suggestions tab (locked comp
// `.impeccable/mocks/decision/documents.html` B, `.chg`): the route-owned
// change queue, each row the change beside what it does to the sign-off
// register, and one line pointing at the proposals Advisory › Learn owns.
// Renders `PendingChanges.transform.ts`; derives nothing.

import { Link, useParams } from 'react-router-dom';
import { Pill } from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { ActorChip } from '../advisory/PipelineStrip';
import { DowntimeCard } from '../downtime/DowntimeCard';
import { CAPS, DOC_ID, DRAFT_PILL, REF } from './documents-ui';
import {
  advisoryPendingLine,
  pendingChangeEffect,
  pendingChanges,
  type PendingChange,
  type PendingChangeEffect,
} from './PendingChanges.transform';

export type PendingChangesProps = {
  /** A row click opens that document on the Library tab. */
  onSelectDocument: (id: string) => void;
};

function Routing({ change }: { change: PendingChange }) {
  if (change.kind === 'revision') {
    return (
      <>
        <ActorChip actor="Agent" />
        <span>drafted from case</span>
        <span className={REF}>{change.sourceCaseLabel}</span>
        <span aria-hidden>→</span>
        <ActorChip actor="Human">{`${change.reviewer} · ${change.seat}`}</ActorChip>
        <span>{change.status}</span>
      </>
    );
  }
  if (!change.flagsDocId) return null;
  return (
    <>
      <span>Flagged</span>
      <span className={REF}>{change.flagsDocId}</span>
      <span>{change.flagsTitle} for revision</span>
    </>
  );
}

function Effect({ effect }: { effect: PendingChangeEffect }) {
  return (
    <div className="flex flex-col gap-1 border-l border-[var(--color-divider)] pl-4 text-[12.5px]" data-change-effect={effect.kind}>
      <span className={CAPS}>{effect.kind === 'on-publish' ? 'On publish' : 'Since publish'}</span>
      <span className="text-[17px] font-semibold tabular-nums text-ink-primary">
        {effect.kind === 'on-publish' ? `+${effect.count}` : effect.count}
        <span className="ml-1.5 text-xs font-medium text-muted">
          {effect.kind === 'on-publish' ? 'sign-offs open' : 'sign-offs stale'}
        </span>
      </span>
      <span className="flex flex-wrap gap-1">
        {effect.people.map((person) => (
          <ActorChip key={person.name} actor="Human">
            {effect.kind === 'on-publish' ? person.name : `${person.name} · ${person.panel}`}
          </ActorChip>
        ))}
      </span>
    </div>
  );
}

export function PendingChanges({ onSelectDocument }: PendingChangesProps) {
  const { projectId } = useParams<{ projectId: string }>();
  const changes = pendingChanges();
  const advisory = advisoryPendingLine();

  if (changes.length === 0) return null;

  return (
    <Commentable name="PendingChanges">
      <DowntimeCard
        title="Pending document changes"
        meta="newest first"
        className="px-0 pb-0 pt-3 [&>header]:mb-0 [&>header]:border-b [&>header]:border-[var(--hairline)] [&>header]:px-4 [&>header]:pb-2.5"
      >
        <ul>
          {changes.map((change) => {
            const effect = pendingChangeEffect(change);
            return (
              <li key={`${change.kind}-${change.docId}-${change.summary}`} className="border-b border-[var(--color-divider)]">
                <button
                  type="button"
                  data-change-kind={change.kind}
                  onClick={() => onSelectDocument(change.docId)}
                  className="grid w-full grid-cols-[minmax(0,1fr)_18.75rem] gap-[18px] px-4 py-3 text-left transition-colors duration-150 hover:bg-[var(--surface-1)]"
                >
                  <span className="min-w-0">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className={DOC_ID}>{change.docId}</span>
                      <span className="text-[13.5px] font-semibold text-ink-primary">{change.title}</span>
                      {change.kind === 'revision' ? (
                        <Pill className={DRAFT_PILL}>Draft — not live until approved</Pill>
                      ) : (
                        <>
                          <span className={REF}>{change.revision}</span>
                          <span className="text-xs text-muted">published {change.at}</span>
                        </>
                      )}
                    </span>
                    <span className="mt-1 block text-[12.5px] leading-[1.45] text-ink-secondary">{change.summary}</span>
                    <span className="mt-[7px] flex flex-wrap items-center gap-1.5 text-[11.5px] text-muted">
                      <Routing change={change} />
                    </span>
                  </span>
                  {effect ? <Effect effect={effect} /> : <span />}
                </button>
              </li>
            );
          })}
        </ul>
        <p className="flex items-center gap-1.5 px-4 py-2.5 text-xs text-muted" data-advisory-line>
          {advisory.lead}
          <Link
            to={`/projects/${projectId ?? 'alpha'}/advisory?tab=learn`}
            className="border-b border-[var(--hairline-strong)] font-semibold text-ink-secondary hover:text-ink-primary"
          >
            {advisory.target}
          </Link>
        </p>
      </DowntimeCard>
    </Commentable>
  );
}
